import type { CommandInput, CommandResult } from "@bb/protocol";
import { ApiError, NetworkError, type Api } from "./api.ts";
import { outboxStore } from "./storage.ts";

export interface OutboxEntry {
  seqNo: number;
  command: CommandInput & { commandId: string };
  createdAt: string;
  attempts: number;
  lastError: string | null;
  /** Sesja pracownika, który utworzył komendę — po blokadzie ekranu nadal jest przypisana do niego. */
  sessionToken: string | null;
}

export interface OutboxFailure {
  command: OutboxEntry["command"];
  result: Extract<CommandResult, { ok: false }>;
}

type Waiter = { resolve: (r: CommandResult) => void };

/**
 * Trwała kolejka komend tabletu. Komenda trafia najpierw do IndexedDB, dopiero
 * potem jest wysyłana. Wysyłka ściśle po kolei (FIFO) — „utwórz zamówienie”
 * zawsze dotrze przed „dopisz pozycje”. Błąd sieci = ponawiamy z tym samym
 * commandId (serwer nie zdubluje), odmowa serwera (4xx) = zgłaszamy obsłudze.
 */
export class Outbox {
  private entries: OutboxEntry[] = [];
  private nextSeq = 1;
  private running = false;
  private paused = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private backoff = 500;
  private waiters = new Map<string, Waiter>();
  private listeners = new Set<() => void>();
  failures: OutboxFailure[] = [];
  /** true, gdy ostatnia próba wysyłki skończyła się błędem sieci. */
  networkDown = false;

  constructor(
    private readonly api: Api,
    private readonly hooks: { onAccepted?: (r: Extract<CommandResult, { ok: true }>) => void; onAuthRequired?: () => void } = {},
  ) {}

  async load(): Promise<void> {
    this.entries = (await outboxStore.all<OutboxEntry>()).sort((a, b) => a.seqNo - b.seqNo);
    this.nextSeq = (this.entries.at(-1)?.seqNo ?? 0) + 1;
    this.emit();
    void this.flush();
  }

  get pending(): readonly OutboxEntry[] {
    return this.entries;
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Dodaje komendę. Zwrócona obietnica rozstrzyga się po odpowiedzi serwera (może to potrwać, gdy nie ma sieci). */
  async enqueue(command: OutboxEntry["command"], sessionToken: string | null): Promise<CommandResult> {
    const entry: OutboxEntry = { seqNo: this.nextSeq++, command, createdAt: new Date().toISOString(), attempts: 0, lastError: null, sessionToken };
    await outboxStore.put(entry); // najpierw zapis na dysk tabletu
    this.entries.push(entry);
    const done = new Promise<CommandResult>((resolve) => this.waiters.set(command.commandId, { resolve }));
    this.emit();
    void this.flush();
    return done;
  }

  /** Wznawia wysyłkę natychmiast (np. po powrocie sieci albo ponownym zalogowaniu). */
  resume(): void {
    this.paused = false;
    this.backoff = 500;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    void this.flush();
  }

  dismissFailure(commandId: string): void {
    this.failures = this.failures.filter((f) => f.command.commandId !== commandId);
    this.emit();
  }

  private async flush(): Promise<void> {
    if (this.running || this.paused) return;
    this.running = true;
    try {
      while (this.entries.length > 0 && !this.paused) {
        const entry = this.entries[0]!;
        entry.attempts += 1;
        let result: CommandResult;
        try {
          result = await this.api.post<CommandResult>("/api/v1/commands", entry.command, entry.sessionToken);
        } catch (e) {
          if (e instanceof ApiError && e.body && e.body.ok === false && e.body.code !== "unauthorized") {
            result = e.body as CommandResult;
          } else if (e instanceof ApiError && e.status === 401) {
            // Sesja wygasła — komendy czekają na ponowne zalogowanie, potem idą z bieżącą sesją. Nic nie ginie.
            entry.lastError = "Zaloguj się ponownie";
            entry.sessionToken = null;
            await outboxStore.put(entry);
            this.paused = true;
            this.emit();
            this.hooks.onAuthRequired?.();
            return;
          } else {
            entry.lastError = e instanceof NetworkError ? "Brak połączenia z serwerem" : String(e);
            this.networkDown = true;
            await outboxStore.put(entry);
            this.emit();
            this.scheduleRetry();
            return;
          }
        }
        this.networkDown = false;
        this.backoff = 500;
        await outboxStore.del(entry.seqNo);
        this.entries.shift();
        if (result.ok) this.hooks.onAccepted?.(result);
        else this.failures.push({ command: entry.command, result });
        this.waiters.get(entry.command.commandId)?.resolve(result);
        this.waiters.delete(entry.command.commandId);
        this.emit();
      }
    } finally {
      this.running = false;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    const delay = this.backoff * (0.75 + Math.random() * 0.5);
    this.backoff = Math.min(this.backoff * 2, 10_000);
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.flush();
    }, delay);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }
}
